/**
 * Engine-hosted order page for paid programs.
 *
 * Route: /programs/:slug/order
 *
 * Reached via the buyer-intent capture on the sales page. URL params:
 *   ?plan=pif|12pay&em=<email>&fn=<first>&ln=<last>
 *
 * Renders: rich TSB-branded order summary card on top, native Spiffy
 * `<spiffy-checkout>` embed below. SpiffyJS shim + identify() traits +
 * URL-param prefill mirror the proven CheckoutPage pattern (which itself
 * mirrors the TSBK-Spiffy HubSpot module, 67+ pages).
 *
 * Config-driven (2026-06-10): plans + checkout URL come from the program's
 * active cohort pricing_overrides via /api/public/programs/:slug `commerce`.
 * Clients retains hardcoded fallbacks so an API hiccup never blocks a sale;
 * programs without commerce config render a not-open card pointing at the
 * RSVP page.
 *
 * One Spiffy product holds both payment plans (PIF + 12-pay); plan choice
 * flows via `?plan=` query param to a Custom Tracking Code script on the
 * Spiffy checkout that calls `checkout.set('payplan', id)` to preselect.
 *
 * Post-purchase, Spiffy redirects the buyer to /programs/:slug/thanks
 * (with the order/name/email URL params Spiffy auto-appends), and the
 * webhook fires server-side to grant Komunily access, enroll the buyer,
 * and queue the welcome email.
 */

import { spiffyUrlString, spiffyPrefillName, withoutSpacedNameParams } from './lib/spiffyUrl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { BRAND_NAME } from './branding';
import type { ProgramPublicResponse } from './ProgramSalesPage';
import { fetchWithRetry } from './lib/fetchRetry';
import { useBrandGtm } from './lib/useBrandGtm';
import { MarketingOptIn } from './components/MarketingOptIn';
import { useOrderGate, withGateParams, type ClaimOutcome } from './lib/useOrderGate';
import { OrderGatePanel, CreditClaimedPanel } from './components/OrderGatePanel';
import { stampVisitorOnAddress } from './attribution';

const API = import.meta.env.VITE_API_BASE_URL as string;

declare global {
  interface Window {
    spiffy?: {
      identify: (traits: Record<string, unknown>) => void;
      config: (opts: Record<string, unknown>) => void;
      load?: (account: string, domain?: string) => void;
      [k: string]: unknown;
    };
  }
}

const SPIFFY_ACCOUNT = 'purebkusa';

/** The Spiffy account a checkout URL lives in, from its `<account>.spiffy.co`
 *  host. Each currency sells from its own account (purebkusa / purebkca /
 *  purebkau), and spiffy.js only renders checkouts of the account it was
 *  loaded for. Loading purebkusa for a purebkca URL left the Clients order
 *  form blank for every CAD and AUD visitor (found 2026-10-04). Same rule as
 *  CheckoutPage's spiffyAccountFromUrl. */
function spiffyAccountFromUrl(spiffyUrl: string): string {
  try {
    const host = new URL(spiffyUrl).hostname.toLowerCase();
    const m = host.match(/^([a-z0-9-]+)\.spiffy\.co$/);
    if (m && m[1] !== 'www' && m[1] !== 'js') return m[1];
  } catch {
    /* fall through */
  }
  return SPIFFY_ACCOUNT;
}
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
// Additional attribution / promo passthrough — these flow alongside UTMs onto
// the Spiffy URL and into identify() traits. `code` is the promo-code param
// Spiffy uses on its hosted checkout URLs (also echoed back in the Thank-You
// URL after purchase). gclid/fbclid carry ad-click attribution.
const EXTRA_PASSTHROUGH_KEYS = ['code', 'gclid', 'fbclid'] as const;

// Brand logo for the Clients program — full-color variant for light-bg headers.
// Sourced from `brand.logoUrl` on /api/public/programs/clients; hardcoded
// here because the order + post-purchase pages don't otherwise fetch brand
// data. R2 URL is stable.
const CLIENTS_PROGRAM_LOGO_URL =
  'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/assets/programs/clients/logo-light.svg';

// ────────────────────────────────────────────────────────────────────────────
// Plan + Spiffy checkout config
//
// Architecture note (2026-05-07): Spiffy doesn't support per-payment-plan SKUs.
// One checkout product holds both plans (PIF + 12-pay); the buyer's plan
// choice flows via `?plan=pif|12pay` URL param into a Custom Tracking Code
// snippet on the Spiffy checkout that calls `checkout.set('payplan', id)`.
//
// Spiffy URL pattern: https://{SD}.spiffy.co/checkout/{url_slug}
// SD = SPIFFY_STORE_SUBDOMAIN = 'purebkusa' (USA account 594).
// url_slug = 'tsb-clients' (registered by Michael 2026-05-12).
// ────────────────────────────────────────────────────────────────────────────

const CLIENTS_SPIFFY_CHECKOUT_URL = 'https://purebkusa.spiffy.co/checkout/tsb-clients';

const CLIENTS_CHECKOUT = {
  pif: {
    name: 'Pay in full',
    priceLabel: '$5,000 USD',
    sub: 'One payment. Save $1,300.',
  },
  '12pay': {
    name: '12 monthly payments',
    priceLabel: '$525 USD/month',
    sub: '$6,300 total over 12 months.',
  },
} as const;

type PlanKey = keyof typeof CLIENTS_CHECKOUT;

interface PlanDisplay {
  name: string;
  priceLabel: string;
  sub: string;
}

// Format a commerce plan (from cohorts.pricing_overrides via
// /api/public/programs/:slug) into the display shape the summary card uses.
// Amounts are whole dollars in the cohort config (Clients: total 5000 = $5,000).
function formatPlan(
  plan: NonNullable<ProgramPublicResponse['commerce']>['plans'][number],
  currency: string,
): PlanDisplay {
  const cur = currency.toUpperCase();
  const money = (n: number) => `$${n.toLocaleString('en-US')} ${cur}`;
  if (plan.per_payment != null) {
    return {
      name: plan.label ?? `${plan.payments ?? 12} monthly payments`,
      priceLabel: `${money(plan.per_payment)}/month`,
      sub: plan.total != null && plan.payments != null
        ? `${money(plan.total)} total over ${plan.payments} months.`
        : '',
    };
  }
  return {
    name: plan.label ?? 'Pay in full',
    priceLabel: money(plan.total ?? 0),
    sub: plan.savings != null ? `One payment. Save $${plan.savings.toLocaleString('en-US')}.` : 'One payment.',
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

export default function ProgramOrderPage() {
  const { slug = 'clients' } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const [data, setData] = useState<ProgramPublicResponse | null>(null);
  const [fetchState, setFetchState] = useState<'loading' | 'done' | 'error'>('loading');
  const [retryNonce, setRetryNonce] = useState(0);

  const plan: PlanKey = useMemo(() => {
    const raw = params.get('plan');
    return raw === '12pay' ? '12pay' : 'pif';
  }, [params]);

  const email = params.get('em') ?? '';
  const firstName = params.get('fn') ?? '';
  const lastName = params.get('ln') ?? '';

  // Region (migration 0163) — multi-region programs route each region to its own
  // cohort/commerce; carry it through so the order page fetches the right pricing.
  const region = params.get('region');
  // Admin/QA currency preview — see sop-ee-clients-multicurrency.md. Forwarded
  // to the public API only when present; a normal buyer's URL never carries
  // it, so this never changes default (IP-resolved) behavior.
  const curParam = params.get('cur');
  // Freedom Gateway is multi-tier: the `plan` param is the tier id ('solo' /
  // 'ultimate'), each routed to its own Spiffy product via per-plan checkoutUrl.
  const isFg = slug === 'freedom-gateway';
  const fgTier = (params.get('plan') || 'solo').toLowerCase();

  // Program config drives the checkout: commerce block (plans + checkout URL)
  // comes from the active cohort's pricing_overrides. Clients keeps its
  // hardcoded constants as a fallback so an API hiccup never blocks a sale.
  // Brand-aware GTM — order pages are the deepest pre-purchase funnel step.
  useBrandGtm(data?.brand?.gtmContainerId, fetchState !== 'loading');
  useEffect(() => {
    let cancelled = false;
    setFetchState('loading');
    (async () => {
      try {
        const qp = new URLSearchParams();
        if (region) qp.set('region', region);
        if (curParam) qp.set('cur', curParam);
        const qs = qp.toString() ? `?${qp.toString()}` : '';
        const res = await fetchWithRetry(`${API}/api/public/programs/${encodeURIComponent(slug)}${qs}`);
        if (!res.ok) {
          if (!cancelled) setFetchState('error');
          return;
        }
        const json = (await res.json()) as ProgramPublicResponse;
        if (!cancelled) {
          setData(json);
          setFetchState('done');
        }
      } catch {
        if (!cancelled) setFetchState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, region, curParam, retryNonce]);

  useEffect(() => {
    const planTitle = slug === 'bkos-license'
      ? 'BKOS License'
      : isFg
        ? `Freedom Gateway — ${fgTier === 'ultimate' ? 'Ultimate' : 'Solo'}`
        : plan === 'pif' ? 'Pay in full' : '12 monthly payments';
    document.title = `Order — ${planTitle} · ${BRAND_NAME}`;
    return () => {
      document.title = BRAND_NAME;
    };
  }, [plan, slug, isFg, fgTier]);

  const isClients = slug === 'clients';
  const isSoda = slug === 'soda-intensive';
  const isBkos = slug === 'bkos-license';

  const commerce = data?.commerce ?? null;
  const commercePlan = useMemo(() => {
    if (!commerce?.plans?.length) return null;
    // BKOS is a single recurring plan (id ends in '-monthly', not '-pif'); take it directly.
    if (isBkos) return commerce.plans[0] ?? null;
    // FG: the plan param is the tier id ('solo' / 'ultimate') — match it exactly.
    if (isFg) return commerce.plans.find((p) => p.id === fgTier) ?? commerce.plans[0] ?? null;
    return commerce.plans.find((p) => p.id?.endsWith(`-${plan}`)) ?? null;
  }, [commerce, plan, isBkos, isFg, fgTier]);
  const selected: PlanDisplay | null = commercePlan
    ? formatPlan(commercePlan, commerce?.currency ?? 'USD')
    : isClients
      ? CLIENTS_CHECKOUT[plan]
      : null;
  // Per-plan checkoutUrl (multi-tier programs route each tier to its own Spiffy
  // product) wins over the cohort-level checkoutUrl.
  const checkoutUrl =
    commercePlan?.checkoutUrl ?? commerce?.checkoutUrl ?? (isClients ? CLIENTS_SPIFFY_CHECKOUT_URL : null);

  const programTitle = data?.program?.display_name ?? data?.program?.name ?? (isClients ? 'Clients Program' : slug);
  const logoUrl = data?.brand?.logoUrl ?? (isClients ? CLIENTS_PROGRAM_LOGO_URL : null);
  const startsLabel = useMemo(() => {
    const raw = isClients ? null : commerce?.cohortStarts;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York',
    });
  }, [commerce, isClients]);

  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Build the Spiffy embed URL. Spiffy's URL-param convention (per their docs)
  // is `email` + `name_first` + `name_last`. UTMs flow through too for
  // attribution. The `plan` param is picked up by a Custom Tracking Code
  // script on the Spiffy checkout that calls `checkout.set('payplan', id)`
  // to preselect the right payment plan — Spiffy doesn't natively support
  // payment-plan preselect via URL params, so we bridge with the JS API.
  const spiffyUrl = useMemo(() => {
    if (!checkoutUrl) return '';
    let u: URL;
    try {
      u = new URL(checkoutUrl);
    } catch {
      return checkoutUrl;
    }
    // BKOS is a single recurring plan; tag it 'monthly'. FG uses the tier id.
    const planCode = isFg ? fgTier : slug === 'bkos-license' ? 'monthly' : plan;
    u.searchParams.set('plan', planCode);
    // Product code for preserved_params matching in the fulfillment recipe.
    // Spiffy captures query params as preserved_params on the order; the recipe's
    // sku_match uses { preserved_param: "spiffy_product_code", equals: "clients-pif" }
    // to route the purchase to the correct fulfillment recipe variant. A plan may
    // carry an explicit `sku` (FG: 'tsb-fgp-solo' / 'tsb-fgp-ultimate').
    u.searchParams.set('spiffy_product_code', commercePlan?.sku ?? `${slug}-${planCode}`);
    if (email) u.searchParams.set('email', email);
    // A spaced name is left off: Spiffy's script turns spaces into "+" (spiffyUrl.ts).
    const fn = spiffyPrefillName(firstName);
    const ln = spiffyPrefillName(lastName);
    if (fn) u.searchParams.set('name_first', fn);
    if (ln) u.searchParams.set('name_last', ln);
    // UTMs flow through for Spiffy's attribution / our webhook payload
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) u.searchParams.set(k, v);
    }
    // Promo code + click IDs — `code` pre-applies on Spiffy's checkout form
    for (const k of EXTRA_PASSTHROUGH_KEYS) {
      const v = params.get(k);
      if (v) u.searchParams.set(k, v);
    }
    return spiffyUrlString(u);
  }, [checkoutUrl, plan, isFg, fgTier, commercePlan, email, firstName, lastName, params]);

  const identifyTraits = useMemo(
    () => buildIdentifyTraits(email, firstName, lastName, params),
    [email, firstName, lastName, params],
  );

  // Entitlement-aware order gate (build F): what this buyer holds and is owed.
  // The member's code (if any) goes onto the Spiffy URL; the form waits for
  // the answer (3s max, then mounts at the normal price).
  const gate = useOrderGate({ email, offerType: 'program', offerRef: slug, plan: isFg ? fgTier : plan });
  const gatedSpiffyUrl = useMemo(() => withGateParams(spiffyUrl, gate.checkoutParams), [spiffyUrl, gate.checkoutParams]);
  const [claimed, setClaimed] = useState<Extract<ClaimOutcome, { ok: true }> | null>(null);

  // Clients always has its hardcoded fallback so it renders instantly; other
  // programs wait for config, then either get the checkout or the not-open card.
  if (!selected || !checkoutUrl) {
    if (fetchState === 'loading') {
      return <div style={loadingStyle}>Loading…</div>;
    }
    // A fetch failure is not the same thing as "this program isn't open" —
    // conflating them told a would-be buyer to go RSVP on a program that was
    // actually open the whole time, just unreachable on that load.
    if (fetchState === 'error') {
      return (
        <div style={loadingStyle}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ margin: '0 0 16px' }}>Hmm — the order page didn't load that time.</p>
            <button
              type="button"
              onClick={() => setRetryNonce((n) => n + 1)}
              style={{
                padding: '10px 24px',
                borderRadius: 8,
                border: 'none',
                background: '#F05A28',
                color: '#fff',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return (
      <div style={pageWrap}>
        <main style={mainWrap}>
          <div style={summaryCard}>
            <h1 style={h1}>Enrollment isn’t open yet.</h1>
            <p style={lede}>
              {programTitle} isn’t taking orders right now. RSVP on the program page and
              you’ll be first to hear when doors open.
            </p>
            <p>
              <Link to={`/programs/${slug}`} style={topNavLink}>
                Go to the program page →
              </Link>
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <header style={topNav}>
        <div style={topNavInner}>
          {logoUrl ? (
            <a href="https://www.thesuccessfulbookkeeper.com" style={topNavLogoLink}>
              <img src={logoUrl} alt={`The Successful Bookkeeper — ${programTitle}`} style={topNavLogo} />
            </a>
          ) : (
            <span />
          )}
          <div style={topNavRight}>
            <Link to={`/programs/${slug}`} style={backLink}>
              ← Back to program details
            </Link>
            {!isSoda && !isBkos && (
              <a href={`/lets-talk/${slug}`} style={topNavLink}>
                Talk with our team
              </a>
            )}
          </div>
        </div>
      </header>

      <main style={mainWrap}>
        <p style={eyebrow}>
          {isClients ? 'Order — Clients Program · First Live Round' : `Order — ${programTitle}`}
        </p>
        <h1 style={h1}>{isSoda ? 'One step from your SODA Intensive.' : isBkos ? 'One step from your BKOS License.' : isFg ? 'One step from your seat in Freedom Gateway.' : 'One step from your seat in the program.'}</h1>
        {claimed ? (
          <p style={lede}>Your free course covers this one. There’s nothing to pay.</p>
        ) : (
          <p style={lede}>
            You’re about to checkout for the <strong>{selected.name}</strong> option. Total today:{' '}
            <strong>{selected.priceLabel}</strong>. {selected.sub}
            {(() => {
              const pct = gate.offers.find((o) => o.applied)?.discount_pct;
              return pct ? <> Your {pct}% member pricing comes off on the order form below.</> : null;
            })()}
          </p>
        )}

        <div style={summaryCard}>
          <div style={summaryRow}>
            <span style={summaryLabel}>Plan</span>
            <span style={summaryVal}>{selected.name}</span>
          </div>
          <div style={summaryRow}>
            <span style={summaryLabel}>Price</span>
            <span style={summaryVal}>{selected.priceLabel}</span>
          </div>
          {isClients ? (
            <>
              <div style={summaryRow}>
                <span style={summaryLabel}>Coaching calls</span>
                <span style={summaryVal}>Live every Wednesday with your coaching team — Lisa, Teresa & Jennifer</span>
              </div>
            </>
          ) : isSoda ? (
            <div style={summaryRow}>
              <span style={summaryLabel}>Format</span>
              <span style={summaryVal}>75-min 1:1 with Ron Bester · book your time after checkout</span>
            </div>
          ) : isBkos ? (
            <>
              <div style={summaryRow}>
                <span style={summaryLabel}>Billing</span>
                <span style={summaryVal}>Monthly subscription · cancel anytime</span>
              </div>
              <div style={summaryRow}>
                <span style={summaryLabel}>Access</span>
                <span style={summaryVal}>Set up for you after checkout · login by email within one business day</span>
              </div>
            </>
          ) : isFg ? (
            <>
              <div style={summaryRow}>
                <span style={summaryLabel}>Billing</span>
                <span style={summaryVal}>12 monthly payments · includes the Pure Bookkeeping System</span>
              </div>
              {(data?.region?.profile?.kickoffLine as string | undefined) && (
                <div style={summaryRow}>
                  <span style={summaryLabel}>Kickoff</span>
                  <span style={summaryVal}>{data?.region?.profile?.kickoffLine as string}</span>
                </div>
              )}
              {(data?.region?.profile?.cadence as string | undefined) && (
                <div style={summaryRow}>
                  <span style={summaryLabel}>Weekly call</span>
                  <span style={summaryVal}>{data?.region?.profile?.cadence as string}</span>
                </div>
              )}
            </>
          ) : (
            startsLabel && (
              <div style={summaryRow}>
                <span style={summaryLabel}>Program starts</span>
                <span style={summaryVal}>{startsLabel}</span>
              </div>
            )
          )}
          {email && (
            <div style={summaryRow}>
              <span style={summaryLabel}>Email on order</span>
              <span style={summaryVal}>{email}</span>
            </div>
          )}
        </div>

        {claimed ? (
          <CreditClaimedPanel outcome={claimed} email={email} />
        ) : (
          <>
            <OrderGatePanel offers={gate.offers} onClaim={gate.claim} onAccept={gate.accept} onClaimed={setClaimed} />
            <p style={embedLead}>Complete your enrollment below.</p>
            {gate.ready && fetchState !== 'loading' ? (
              <>
                <div
                  ref={wrapRef}
                  className="spiffy-checkout-wrap"
                  data-url={spiffyUrl}
                  style={embedWrapStyle}
                />
                <SpiffyEmbedMount wrapRef={wrapRef} url={gatedSpiffyUrl} identifyTraits={identifyTraits} />
              </>
            ) : (
              <p style={embedLead}>Preparing your order form…</p>
            )}
          </>
        )}

        {!claimed && (
          <p style={fineprint}>
            Your card is processed securely by Spiffy — never stored on this site. Once payment is
            confirmed, you’ll land on your registration page automatically.
          </p>
        )}
        {!claimed && (commerce as { taxNote?: string } | null)?.taxNote && (
          <p style={fineprint}>{(commerce as { taxNote?: string }).taxNote}</p>
        )}
        {email && /^\S+@\S+\.\S+$/.test(email) && (
          <MarketingOptIn
            email={email}
            firstName={firstName || null}
            source="program-register"
            sourceDetail={`${slug}-order`}
            programSlug={slug}
            brandLabel={data?.brand?.name ?? BRAND_NAME}
            style={{ marginTop: -8, marginBottom: 20 }}
          />
        )}
      </main>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Inline styles
// ────────────────────────────────────────────────────────────────────────────

const PROG_PRIMARY = 'var(--prog-primary, #FF6B35)';
const PROG_DEEP = 'var(--prog-deep, #1A1A3E)';
const PROG_TEXT_DARK = 'var(--prog-text-dark, #0A0A2E)';
const PROG_CANVAS = 'var(--prog-canvas, #F8F6F3)';
const FONT_HEADING = 'var(--font-heading, "Fraunces", Georgia, serif)';
const FONT_BODY = 'var(--font-body, "Poppins", -apple-system, sans-serif)';

const pageWrap: React.CSSProperties = {
  minHeight: '100vh',
  background: PROG_CANVAS,
  color: PROG_TEXT_DARK,
  fontFamily: FONT_BODY,
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  fontFamily: FONT_BODY,
  color: PROG_TEXT_DARK,
};

const topNav: React.CSSProperties = {
  background: '#FFFFFF',
  borderBottom: '1px solid rgba(10,10,46,0.08)',
  padding: '12px 24px',
};
const topNavInner: React.CSSProperties = {
  maxWidth: '1140px',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
};
const topNavLogoLink: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
};
const topNavLogo: React.CSSProperties = {
  height: '36px',
  width: 'auto',
  display: 'block',
};
const topNavRight: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
};
const topNavLink: React.CSSProperties = {
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  color: PROG_PRIMARY,
  textDecoration: 'none',
  borderBottom: `2px solid ${PROG_PRIMARY}`,
  paddingBottom: '1px',
};

const backLink: React.CSSProperties = {
  fontSize: '13px',
  color: PROG_DEEP,
  textDecoration: 'none',
  fontWeight: 600,
  opacity: 0.7,
};

const mainWrap: React.CSSProperties = {
  maxWidth: '960px',
  margin: '0 auto',
  padding: '56px 24px 80px',
};

const eyebrow: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: PROG_PRIMARY,
  margin: '0 0 12px',
};

const h1: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: 'clamp(28px, 4vw, 38px)',
  fontWeight: 600,
  lineHeight: 1.15,
  margin: '0 0 14px',
};

const lede: React.CSSProperties = {
  fontSize: '17px',
  lineHeight: 1.55,
  color: PROG_TEXT_DARK,
  opacity: 0.8,
  margin: '0 0 32px',
};

const summaryCard: React.CSSProperties = {
  background: '#fff',
  border: '1px solid rgba(10,10,46,0.08)',
  borderRadius: 'var(--radius-card, 10px)',
  padding: '24px 26px',
  marginBottom: '24px',
  boxShadow: '0 2px 12px rgba(10,10,46,0.04)',
};

const summaryRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '16px',
  padding: '12px 0',
  borderBottom: '1px solid rgba(10,10,46,0.06)',
  fontSize: '15px',
};

const summaryLabel: React.CSSProperties = {
  fontWeight: 600,
  color: PROG_TEXT_DARK,
  opacity: 0.7,
  letterSpacing: '0.01em',
  flexShrink: 0,
};

const summaryVal: React.CSSProperties = {
  color: PROG_TEXT_DARK,
  textAlign: 'right',
};

const embedLead: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: '20px',
  fontWeight: 600,
  color: PROG_TEXT_DARK,
  margin: '0 0 16px',
};

const embedWrapStyle: React.CSSProperties = {
  // Spiffy's embed brings its own chrome; we let it size itself within the wrap.
  marginBottom: '20px',
  minHeight: '200px',
};

const fineprint: React.CSSProperties = {
  fontSize: '13px',
  lineHeight: 1.55,
  color: PROG_TEXT_DARK,
  opacity: 0.65,
  textAlign: 'center',
  marginBottom: '32px',
};

// ────────────────────────────────────────────────────────────────────────────
// Spiffy embed plumbing
//
// Adopts the proven pattern from CheckoutPage.tsx (which itself mirrors the
// TSBK-Spiffy HubSpot module, 67+ pages). Critical ordering: spiffy.load()
// runs AFTER the <spiffy-checkout> element is appended, so SpiffyJS's
// initial DOM scan finds and renders it.
// ────────────────────────────────────────────────────────────────────────────

/** Build the identify() trait map for SpiffyJS. Names sent in both camelCase
 *  and snake_case variants — Spiffy's docs aren't explicit on property names
 *  so we cover both forms. */
function buildIdentifyTraits(
  email: string,
  firstName: string,
  lastName: string,
  params: URLSearchParams,
): Record<string, string> | null {
  if (!email) return null;
  const traits: Record<string, string> = { email, email_address: email };
  if (firstName) {
    traits.first_name = firstName;
    traits.firstName = firstName;
    traits.given_name = firstName;
    traits.givenName = firstName;
  }
  if (lastName) {
    traits.last_name = lastName;
    traits.lastName = lastName;
    traits.family_name = lastName;
    traits.familyName = lastName;
  }
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) traits[k] = v;
  }
  for (const k of EXTRA_PASSTHROUGH_KEYS) {
    const v = params.get(k);
    if (v) traits[k] = v;
  }
  return traits;
}

/** Inject the SpiffyJS shim (no-op if already present). The shim queues
 *  identify() / config() calls until spiffy.js hydrates and replays them. */
function ensureSpiffyShim(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  const f = (w.spiffy = w.spiffy || []);
  if (f.init || f.invoked) return; // already initialized
  f.invoked = true;
  f.methods = ['identify', 'config', 'reset', 'debug', 'off', 'on'];
  f.factory = (i: string) => function (...args: unknown[]) {
    args.unshift(i);
    f.push(args);
    return f;
  };
  f.methods.forEach((m: string) => {
    w.spiffy[m] = f.factory(m);
  });
  f.load = (acct: string) => {
    if (w.spiffy.ACCOUNT) return; // idempotent
    w.spiffy.ACCOUNT = acct;
    const e = document.createElement('script');
    e.type = 'text/javascript';
    e.async = true;
    (e as HTMLScriptElement & { crossorigin: string }).crossorigin = 'anonymous';
    e.src = `https://js.static.spiffy.co/spiffy.js?a=${acct}`;
    const n = document.getElementsByTagName('script')[0];
    n.parentNode?.insertBefore(e, n);
  };
  f.SNIPPET_VERSION = '1.1.0';
}

/** Mount the Spiffy embed. Sequence:
 *   1. ensureSpiffyShim — defines window.spiffy queue if missing
 *   2. spiffy.identify(traits) + spiffy.config — queued
 *   3. document.createElement('spiffy-checkout'), setAttribute('url'), append
 *   4. spiffy.load(account) — injects spiffy.js, which scans + renders
 *  Because (4) happens AFTER (3), the element is in the DOM when SpiffyJS
 *  does its initial scan. spiffy.load() is idempotent. */
// The embed mounts only once the program config has loaded: Clients renders
// instantly on its hardcoded USD checkout URL, and spiffy.load() locks the
// account at first call, so mounting early left every CAD/AUD visitor with a
// blank form (their real URL arrived after the account was already purebkusa).
function SpiffyEmbedMount({
  wrapRef,
  url,
  identifyTraits,
}: {
  wrapRef: React.MutableRefObject<HTMLDivElement | null>;
  url: string;
  identifyTraits: Record<string, string> | null;
}) {
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || !url) return;

    // Spiffy copies this page's own query into the form; a spaced name there
    // arrives as "Mary+Ann". Drop it from the address first (spiffyUrl.ts).
    const cleaned = withoutSpacedNameParams(window.location.href);
    if (cleaned) window.history.replaceState(window.history.state, '', cleaned);
    ensureSpiffyShim();

    if (identifyTraits && window.spiffy) window.spiffy.identify(identifyTraits);
    if (window.spiffy) window.spiffy.config({ hideSidebar: false });

    // Idempotent — strip any prior children before append.
    while (wrap.firstChild) wrap.removeChild(wrap.firstChild);

    // Spiffy keeps this page's address on the order; the browser id rides in it
    // so the purchase webhook can tie this browser to the buyer.
    stampVisitorOnAddress();

    const el = document.createElement('spiffy-checkout');
    el.setAttribute('url', url);
    wrap.appendChild(el);

    if (window.spiffy?.load) window.spiffy.load(spiffyAccountFromUrl(url));

    return () => {
      // Cleanup so SPA navigation doesn't leak the embed iframe.
      while (wrap.firstChild) wrap.removeChild(wrap.firstChild);
    };
  }, [wrapRef, url, identifyTraits]);
  return null;
}
